import { t } from 'i18next';

import { BomPartType } from 'bom/enums';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { WarningType } from 'core/enums';
import { getPlainTextLength } from 'core/utils';
import { ShipmentCode } from 'corporate/enums';
import { CorporateLocationHelper } from 'corporate/helpers';
import { ALTERNATE_PART_NUMBER_MAX_LENGTH } from 'parts/constants';
import { ExternalPartStatusType } from 'parts/enums';
import { PartNumberHelper } from 'parts/helpers';
import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { NOTE_DEFAULT_MAX_LENGTH, NOTE_SUPSHIP_MAX_LENGTH } from 'quotes/constants';
import {
  PriceBasedOn,
  QuoteEntityType,
  QuotePartNoteType,
  QuotePartPriceGuidanceSourceType,
  QuotePartRecordType,
  ValidationStatus,
  ValidationType,
} from 'quotes/enums';
import { ICustomerQuotePart, IQuotePart, IQuotePartExtraData } from 'quotes/types';
import { isSelectedSpaSource } from 'quotes/utils';

export const QuotePartHelper = {
  quantityValidationStatuses: [
    ValidationType.QUANTITY_UOM_VALIDATION_STATUS,
    ValidationType.ORDER_INCREMENT_VALIDATION_STATUS,
    ValidationType.MOQ_VALIDATION_STATUS,
  ],
  costValidationStatuses: [ValidationType.COST_VALIDATION_STATUS],
  marginValidationStatuses: [ValidationType.GPM_VALIDATION_STATUS],

  shipmentTypeWarning: (data: IQuotePart) => {
    const showWarning = !!(data?.alternateVersionEnabled && data?.shipmentTypeWarning);

    return {
      show: showWarning,
      label: showWarning ? t('warningPartNotSetAtLocation') : null,
    };
  },

  // The two UOM-cell warnings are independent and can both apply to one line, so they share a
  // single icon and stack into one tooltip. externalUomConversionMissing is not gated on
  // alternateVersionEnabled here — the BE already applies that gate before sending it.
  uomWarnings: (data: IQuotePart): string[] | null => {
    const warnings: string[] = [];

    if (data?.uomMismatch) {
      warnings.push(t('warningUomMismatch'));
    }

    if (data?.externalUomConversionMissing) {
      warnings.push(t('warningExternalUomConversionMissing'));
    }

    return warnings.length ? warnings : null;
  },

  hasUomWarning: (data: IQuotePart): boolean => !!QuotePartHelper.uomWarnings(data),

  // Nightly accrual window (869fg3r09, temporary): the line has no cost because the pricing engine
  // is paused right now. Like externalUomConversionMissing, the BE has already applied every gate,
  // so the flag alone decides. Its message names the company's own window hours; the translation
  // is only the fallback for a flag that arrives without one.
  pricingWindowWarning: (data: IQuotePart): string | null =>
    data?.externalPricingWindowClosed
      ? data.externalPricingWindowMessage || t('warningExternalPricingWindowClosed')
      : null,

  // Quote-level: the engine is paused for the whole quote, so Select SPA and its removal wait.
  quotePricingWindowMessage: (extraData: IQuotePartExtraData | null | undefined): string | null =>
    extraData?.pricingWindowClosed
      ? extraData.pricingWindowMessage || t('warningExternalPricingWindowClosed')
      : null,

  // The quote-page banner: the sentence of the first loaded line the window left without a cost.
  // Same test as the line list, so a row that gained a cost locally before the next read refreshed
  // the flag does not keep the banner up.
  pricingWindowBanner: (records: IQuotePart[]): string | null => {
    for (const record of records ?? []) {
      const warning =
        record?.deleted || QuotePartHelper.isCostPresent(record as ICustomerQuotePart)
          ? null
          : QuotePartHelper.pricingWindowWarning(record);

      if (warning) {
        return warning;
      }
    }

    return null;
  },

  isNotValid: (data: IQuotePart, quoteEntityType = QuoteEntityType.QUOTE) => {
    return (
      Object.values(ValidationType).some(
        (status) => ValidationStatus.NOT_VALID === data?.[status]
      ) ||
      data?.uomMismatch ||
      data?.externalUomConversionMissing ||
      !QuotePartHelper.isVendorQuoteNumberValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isReplacementCostInfoValid(data as ICustomerQuotePart, quoteEntityType) ||
      !QuotePartHelper.isExternalPartStatusValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isShippingLocationValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isTaxonomyValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isVendorValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isManufacturerAllowed(data as ICustomerQuotePart) ||
      !QuotePartHelper.isQuantityDecimalValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isPartNumberLengthValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isDescriptionPresent(data as ICustomerQuotePart) ||
      !QuotePartHelper.isCostPresent(data as ICustomerQuotePart) ||
      !QuotePartHelper.isPricePresent(data as ICustomerQuotePart) ||
      !QuotePartHelper.isShipmentTypePresent(data as ICustomerQuotePart) ||
      !QuotePartHelper.isManufacturerPresent(data as ICustomerQuotePart) ||
      !QuotePartHelper.isBuyerNoteValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isPickerNoteValid(data as ICustomerQuotePart) ||
      !QuotePartHelper.isSupshipNoteValid(data as ICustomerQuotePart)
    );
  },

  validity: (data: IQuotePart) =>
    QuotePartHelper.isNotValid(data) ? ValidationStatus.NOT_VALID : ValidationStatus.VALID,

  validationErrors: ({
    data,
    statuses = Object.values(ValidationType),
    withValues = true,
    filteredByStatuses = true,
    quoteEntityType = QuoteEntityType.QUOTE,
  }: {
    data: IQuotePart;
    statuses?: ValidationType[];
    withValues?: boolean;
    filteredByStatuses?: boolean;
    quoteEntityType?: QuoteEntityType;
  }) => {
    const errors: string[] = [];

    statuses.map((statusField) => {
      if (ValidationStatus.NOT_VALID === data?.[statusField]) {
        errors.push(
          QuotePartHelper.mapValidationErrors(
            statusField,
            withValues,
            data?.corporatePartConfig?.minimumQuantityInQuoteUom,
            data?.corporatePartConfig?.orderIncrementInQuoteUom
          )
        );
      }
    });

    if (filteredByStatuses) {
      return errors.length ? errors : null;
    }

    if (data?.uomMismatch) {
      errors.push(t('warningValidationUom'));
    }

    if (data?.externalUomConversionMissing) {
      errors.push(t('warningValidationExternalUomConversion'));
    }

    if (!QuotePartHelper.isVendorQuoteNumberValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationVendorQuoteNumber'));
    }

    if (!QuotePartHelper.isReplacementCostInfoValid(data as ICustomerQuotePart, quoteEntityType)) {
      errors.push(t('warningValidationReplacementCostInfo'));
    }

    if (!QuotePartHelper.isExternalPartStatusValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationExternalPartStatus'));
    }

    if (!QuotePartHelper.isShippingLocationValid(data as ICustomerQuotePart)) {
      errors.push(
        (data as ICustomerQuotePart)?.corporateLocation?.id
          ? t('warningValidationShippingLocation')
          : t('warningValidationShippingLocationNotSet')
      );
    }

    if (!QuotePartHelper.isTaxonomyValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationTaxonomy'));
    }

    if (!QuotePartHelper.isVendorValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationVendor'));
    }

    if (!QuotePartHelper.isManufacturerAllowed(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationDisallowedManufacturer'));
    }

    if (!QuotePartHelper.isQuantityDecimalValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationQuantityDecimal'));
    }

    if (!QuotePartHelper.isPartNumberLengthValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationPartNumberTooLong'));
    }

    if (!QuotePartHelper.isDescriptionPresent(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationMissingDescription'));
    }

    if (!QuotePartHelper.isCostPresent(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationMissingCost'));

      // Says why the cost is missing. Listed only beside the missing cost, so a row that gained a
      // cost locally before the next read refreshed the flag does not keep the sentence.
      const pricingWindowWarning = QuotePartHelper.pricingWindowWarning(data);

      if (pricingWindowWarning) {
        errors.push(pricingWindowWarning);
      }
    }

    if (!QuotePartHelper.isPricePresent(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationMissingPrice'));
    }

    if (!QuotePartHelper.isShipmentTypePresent(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationMissingShipmentType'));
    }

    if (!QuotePartHelper.isManufacturerPresent(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationMissingManufacturer'));
    }

    if (!QuotePartHelper.isBuyerNoteValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationBuyerNoteTooLong'));
    }

    if (!QuotePartHelper.isPickerNoteValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationPickerNoteTooLong'));
    }

    if (!QuotePartHelper.isSupshipNoteValid(data as ICustomerQuotePart)) {
      errors.push(t('warningValidationSupshipNoteTooLong'));
    }

    return errors.length ? errors : null;
  },

  mapValidationErrors: (
    validationType: ValidationType,
    withValues: boolean,
    minimumQuantityInQuoteUom?: number,
    orderIncrementInQuoteUom?: number
  ) => {
    switch (validationType) {
      case ValidationType.COST_VALIDATION_STATUS:
        return t('errorInvalidCost');
      case ValidationType.GPM_VALIDATION_STATUS:
        return t('warningValidationGpm');
      case ValidationType.MOQ_VALIDATION_STATUS:
        return withValues
          ? t('warningValidationQuantityBelowMinimum', {
              minimum: minimumQuantityInQuoteUom,
            })
          : t('warningValidationMoq');
      case ValidationType.ORDER_INCREMENT_VALIDATION_STATUS:
        return withValues
          ? t('warningValidationQuantityNotMultipleOfIncrement', {
              increment: orderIncrementInQuoteUom,
            })
          : t('warningValidationOrderIncrement');
      case ValidationType.QUANTITY_UOM_VALIDATION_STATUS:
        return t('warningValidationQuantityUom');
      default:
        return '';
    }
  },

  isValidated: (data: IQuotePart, statuses = Object.values(ValidationType)) =>
    statuses.some((statusField) => ValidationStatus.VALIDATED_BY_USER === data?.[statusField]) &&
    statuses.every((statusField) => ValidationStatus.NOT_VALID !== data?.[statusField]),

  validationWarnings: (
    data: IQuotePart,
    statuses = Object.values(ValidationType),
    filteredByStatuses = true
  ) => {
    const errors = QuotePartHelper.validationErrors({
      data,
      statuses,
      filteredByStatuses,
    });

    if (errors) {
      return errors;
    }

    const isValidated = QuotePartHelper.isValidated(data, statuses);
    if (isValidated) {
      return t('warningValidatedByUser');
    }

    return null;
  },

  quantityValidationWarnings: (data: IQuotePart) =>
    QuotePartHelper.validationWarnings(data, QuotePartHelper.quantityValidationStatuses),

  costValidationWarnings: (data: IQuotePart) =>
    QuotePartHelper.validationWarnings(data, QuotePartHelper.costValidationStatuses),

  marginValidationWarnings: (data: IQuotePart) =>
    QuotePartHelper.validationWarnings(data, QuotePartHelper.marginValidationStatuses),

  shippingLocationValidationWarnings: (data: IQuotePart) => {
    if (!data) {
      return null;
    }

    if (!QuotePartHelper.isShippingLocationValid(data as ICustomerQuotePart)) {
      return (data as ICustomerQuotePart).corporateLocation?.id
        ? t('warningValidationShippingLocation')
        : t('warningValidationShippingLocationNotSet');
    }

    return null;
  },

  warningType: (data: IQuotePart, statuses = Object.values(ValidationType)) => {
    if (QuotePartHelper.isValidated(data, statuses)) {
      return WarningType.VALIDATED;
    }

    return WarningType.ERROR;
  },

  quantityWarningType: (data: IQuotePart) =>
    QuotePartHelper.warningType(data, QuotePartHelper.quantityValidationStatuses),

  costWarningType: (data: IQuotePart) =>
    QuotePartHelper.warningType(data, QuotePartHelper.costValidationStatuses),

  marginWarningType: (data: IQuotePart) =>
    QuotePartHelper.warningType(data, QuotePartHelper.marginValidationStatuses),

  hasWarnings: (data: IQuotePart, statuses = Object.values(ValidationType)) => {
    const warningStatuses = [ValidationStatus.NOT_VALID, ValidationStatus.VALIDATED_BY_USER];

    return statuses.some((statusField) => warningStatuses.includes(data?.[statusField]));
  },

  quantityHasWarnings: (data: IQuotePart) =>
    QuotePartHelper.hasWarnings(data, QuotePartHelper.quantityValidationStatuses),

  costHasWarnings: (data: IQuotePart) =>
    QuotePartHelper.hasWarnings(data, QuotePartHelper.costValidationStatuses),

  marginHasWarnings: (data: IQuotePart) =>
    QuotePartHelper.hasWarnings(data, QuotePartHelper.marginValidationStatuses),

  getVendorQuoteNumber: ({
    costSourceQuoteExternalNumber = null,
    costSourceQuoteId = null,
    costSourceType = null,
    costSourceValidSince = null,
    costSourceVendorQuoteNumber = null,
  }: {
    costSourceQuoteExternalNumber?: string;
    costSourceQuoteId?: number;
    costSourceType?: PricingSourceType;
    costSourceValidSince?: string;
    costSourceVendorQuoteNumber?: string;
  } = {}): string => {
    if (costSourceQuoteId) {
      return costSourceQuoteExternalNumber || costSourceQuoteId?.toString();
    }

    if (costSourceVendorQuoteNumber) {
      return costSourceVendorQuoteNumber;
    }

    if (PricingSourceType.CORPORATE === costSourceType) {
      return `PriceList-${costSourceValidSince}`;
    }

    return null;
  },

  isVendorQuoteNumberValid: (data: ICustomerQuotePart) => {
    if (!data?.alternateVersionEnabled || !data?.computedPurchaseCost) {
      return true;
    }

    return PricingSourceType.CUSTOM !== data.costSourceType || !!data.costSourceVendorQuoteNumber;
  },

  isReplacementCostInfoValid: (
    data: ICustomerQuotePart,
    quoteEntityType = QuoteEntityType.QUOTE
  ) => {
    // A picked agreement is submitted without either value, as in SubmitCustomerOrder/utils.tsx.
    if (isSelectedSpaSource(data?.costSourceType)) {
      return true;
    }

    if (
      !data?.alternateVersionEnabled ||
      quoteEntityType !== QuoteEntityType.ORDER ||
      !data?.computedPurchaseCost ||
      !data?.supplier?.isShipAndDebit ||
      !!data?.externalReplacementCost
    ) {
      return true;
    }

    const purchaseCost = data?.computedPurchaseCost;
    const replacementCost = data?.replacementCost;

    const isNonStockShipment = QuotePartHelper.isNonStockShipment({
      code: data?.shipmentType?.code,
    });

    return isNonStockShipment
      ? !!replacementCost &&
          (purchaseCost >= replacementCost ||
            (purchaseCost < replacementCost && !!data?.replacementCostNumber))
      : true;
  },

  isExternalPartStatusValid: (data: ICustomerQuotePart) => {
    if (!data?.alternateVersionEnabled || !data?.externalPartStatus) {
      return true;
    }

    return data.externalPartStatus !== ExternalPartStatusType.ABANDONED;
  },

  isShippingLocationValid: (data: Partial<ICustomerQuotePart>): boolean => {
    const isAlternateVersionEnabled = Boolean(data.alternateVersionEnabled);
    const corporateLocation = data.corporateLocation;

    if (!corporateLocation) {
      return !isAlternateVersionEnabled;
    }

    return CorporateLocationHelper.isShipping(corporateLocation, isAlternateVersionEnabled);
  },

  isTaxonomyValid: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !!data.taxonomyGroupId;
  },

  isVendorValid: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !!data.accountsPayableId;
  },

  isManufacturerAllowed: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled || !data?.partArr?.manufacturerId) {
      return true;
    }

    return !!data.alternateManufacturerName;
  },

  isQuantityDecimalValid: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }
    if (data.quantity === null || data.quantity === undefined) {
      return true;
    }

    return Number.isInteger(data.quantity);
  },

  isPartNumberLengthValid: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled || !data?.partArr?.number) {
      return true;
    }

    return data.partArr.number.length <= ALTERNATE_PART_NUMBER_MAX_LENGTH;
  },

  isDescriptionPresent: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !!(data.partArr?.description || data.partArr?.descriptionLong);
  },

  isCostPresent: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !PricingHelper.isEmpty(data.cost) && !!data.costSourceId;
  },

  isPricePresent: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !PricingHelper.isEmpty(data.price) && !!data.priceSourceId;
  },

  isShipmentTypePresent: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !!data.shipmentTypeId;
  },

  isManufacturerPresent: (data: ICustomerQuotePart): boolean => {
    if (!data?.alternateVersionEnabled) {
      return true;
    }

    return !!data.partArr?.manufacturerId;
  },

  isNoteTypeWithinLimit: (
    data: ICustomerQuotePart,
    type: QuotePartNoteType,
    maxLength: number
  ): boolean => {
    if (!data?.alternateVersionEnabled || !data?.notes) {
      return true;
    }

    return !Object.values(data.notes).some(
      (note) => note.type === type && getPlainTextLength(note.content) > maxLength
    );
  },

  isBuyerNoteValid: (data: ICustomerQuotePart): boolean =>
    QuotePartHelper.isNoteTypeWithinLimit(data, QuotePartNoteType.BUYER, NOTE_DEFAULT_MAX_LENGTH),

  isPickerNoteValid: (data: ICustomerQuotePart): boolean =>
    QuotePartHelper.isNoteTypeWithinLimit(data, QuotePartNoteType.PICKER, NOTE_DEFAULT_MAX_LENGTH),

  isSupshipNoteValid: (data: ICustomerQuotePart): boolean =>
    QuotePartHelper.isNoteTypeWithinLimit(data, QuotePartNoteType.SUPSHIP, NOTE_SUPSHIP_MAX_LENGTH),

  isCostLocked: (data: ICustomerQuotePart) => {
    if (
      !data ||
      !data.alternateVersionEnabled ||
      PricingSourceType.EXTERNAL !== data.costSourceType
    ) {
      return false;
    }

    return !!data.externalCostLocked;
  },

  isPrioritizingLegacyCost: (data: ICustomerQuotePart) => {
    if (!data || !data.alternateVersionEnabled) {
      return false;
    }

    return !!data.prioritizeLegacyCost;
  },

  isPriceLocked: (data: ICustomerQuotePart) => {
    if (
      !data ||
      !data.alternateVersionEnabled ||
      PricingSourceType.EXTERNAL !== data.priceSourceType
    ) {
      return false;
    }

    return !!data.externalPriceLocked;
  },

  convertPriceSourceToPricingGuidanceSourceType: (source: PricingSourceType) => {
    switch (source) {
      case PricingSourceType.CUSTOM:
        return QuotePartPriceGuidanceSourceType.MANUALLY_ENTERED;
      case PricingSourceType.EXTERNAL:
        return QuotePartPriceGuidanceSourceType.EXTERNAL;
      default:
        return QuotePartPriceGuidanceSourceType.LEGACY;
    }
  },

  pricingLabel: ({
    source,
    isAlternateVersionEnabled = false,
    hasColon = true,
  }: {
    source: QuotePartPriceGuidanceSourceType;
    isAlternateVersionEnabled?: boolean;
    hasColon?: boolean;
  }) => {
    if (!isAlternateVersionEnabled) {
      return null;
    }

    switch (source) {
      case QuotePartPriceGuidanceSourceType.MANUALLY_ENTERED:
        return `${t('labelManuallyEnteredPrice')}${hasColon ? ':' : ''}`;
      case QuotePartPriceGuidanceSourceType.EXTERNAL:
        return `${t('labelRecommendedPrice')}${hasColon ? ':' : ''}`;
      case QuotePartPriceGuidanceSourceType.SALES_ORDER_HISTORY:
        return `${t('labelLatestPrice')}${hasColon ? ':' : ''}`;
      case QuotePartPriceGuidanceSourceType.LEGACY:
      default:
        return `${t('labelProfilePrice')}${hasColon ? ':' : ''}`;
    }
  },

  displayReplacementCost: ({
    isAlternateVersionEnabled,
    isShipAndDebitVendor,
  }: {
    isAlternateVersionEnabled: boolean;
    isShipAndDebitVendor: boolean;
  }) => isAlternateVersionEnabled && isShipAndDebitVendor,

  displayCustomCostSourceVendorQuoteNumber: ({
    isAlternateVersionEnabled,
    costSourceType,
  }: {
    isAlternateVersionEnabled: boolean;
    costSourceType: PricingSourceType;
  }) => isAlternateVersionEnabled && costSourceType === PricingSourceType.CUSTOM,

  isNonStockShipment: ({ code }: { code?: ShipmentCode }) => !!code && code !== ShipmentCode.STOCK,

  allowOverrideReplacementCost: ({
    isAlternateVersionEnabled,
    isShipAndDebitVendor,
    shipmentTypeCode,
  }: {
    isAlternateVersionEnabled: boolean;
    isShipAndDebitVendor: boolean;
    shipmentTypeCode: ShipmentCode;
  }) =>
    isAlternateVersionEnabled &&
    isShipAndDebitVendor &&
    QuotePartHelper.isNonStockShipment({ code: shipmentTypeCode }),

  getConsensusPriceBasedOn: (quoteParts: ICustomerQuotePart[]): PriceBasedOn => {
    if (!quoteParts?.length) {
      return PriceBasedOn.QUOTED;
    }

    const first = quoteParts[0]?.priceBasedOn || PriceBasedOn.QUOTED;
    const allAgree = quoteParts.every(
      (quotePart) => (quotePart?.priceBasedOn || PriceBasedOn.QUOTED) === first
    );

    return allAgree ? first : PriceBasedOn.QUOTED;
  },

  isEligibleForReplacementPricing: (data: ICustomerQuotePart) => {
    if (!data?.supplier?.isShipAndDebit) {
      return false;
    }

    if (QuotePartHelper.isPriceLocked(data)) {
      return false;
    }

    // Family A (raw replacement cost), not the derived standard cost: the std cost is
    // now materialized lazily at need-points, so a line qualifies for replacement pricing
    // as soon as a manual or external replacement cost exists.
    return Number(data?.replacementCost) > 0 || Number(data?.externalReplacementCost) > 0;
  },

  requiredReplacementCost: ({
    isAlternateVersionEnabled,
    isShipAndDebitVendor,
    purchaseCost,
  }: {
    isAlternateVersionEnabled: boolean;
    isShipAndDebitVendor: boolean;
    purchaseCost?: number;
  }) => isAlternateVersionEnabled && isShipAndDebitVendor && !!purchaseCost,

  requiredVendorQuoteNumber: ({
    isAlternateVersionEnabled,
    isCostingCustom,
    purchaseCost,
  }: {
    isAlternateVersionEnabled: boolean;
    isCostingCustom: boolean;
    purchaseCost?: number;
  }) => isAlternateVersionEnabled && isCostingCustom && !!purchaseCost,

  requiredReplacementCostNumber: ({
    isAlternateVersionEnabled,
    isShipAndDebitVendor,
    purchaseCost,
    replacementCost,
  }: {
    isAlternateVersionEnabled: boolean;
    isShipAndDebitVendor: boolean;
    purchaseCost?: number;
    replacementCost?: number;
  }) => {
    return (
      isAlternateVersionEnabled &&
      isShipAndDebitVendor &&
      !!purchaseCost &&
      !!replacementCost &&
      purchaseCost < replacementCost
    );
  },

  getDisplayedPartNumber: (quotePart: IQuotePart) => {
    if (1 === quotePart.partArr?.generic) {
      return DEFAULT_PLACEHOLDER;
    }

    if (BomPartType.DISCOUNT === quotePart.type && !quotePart.partArr?.id) {
      return QuotePartRecordType.PRICING_GROUP_DISCOUNT === quotePart?.recordType
        ? quotePart?.pricingGroupDiscountName
        : quotePart?.partDiscountNumber;
    }

    return PartNumberHelper.toLabel(quotePart.partArr?.number);
  },

  // Stored number (underscores kept) for real parts; placeholders and discount names as displayed.
  getLinePartNumber: (quotePart: IQuotePart) => {
    const partNumber = quotePart.partArr?.number;
    const displayedPartNumber = QuotePartHelper.getDisplayedPartNumber(quotePart);

    return PartNumberHelper.toLabel(partNumber) === displayedPartNumber
      ? partNumber ?? null
      : displayedPartNumber;
  },
};

export default QuotePartHelper;
